// Implements: F-PRV-007
/**
 * The `preview` module's client, on top of the single HTTP exit.
 *
 * `GET /mockups/{id}/preview/{version_id}/source` answers the three stored pieces of one version
 * and the version they belong to; the viewer shows them only beside that version (`SourceTabs`
 * renders them as text). A `GET` is idempotent, so the client's retry of a 5xx applies.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";

/** The shape `PreviewContainer` takes as `source`: the pieces and the version they belong to. */
export interface VersionSource {
  readonly versionId: string;
  readonly pieces: { readonly html: string; readonly css: string; readonly js: string };
}

interface VersionSourceView {
  readonly version_id: string;
  readonly pieces: { readonly html: string; readonly css: string; readonly js: string };
}

export function versionSourcePath(mockupId: string, versionId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/preview/${encodeURIComponent(versionId)}/source`;
}

export async function fetchVersionSource(
  mockupId: string,
  versionId: string,
): Promise<VersionSource> {
  const body = await httpRequest<VersionSourceView>(versionSourcePath(mockupId, versionId));
  if (body === undefined) {
    throw new ApiError(
      200,
      UNKNOWN_ERROR_CODE,
      "GET /mockups/{id}/preview/{version_id}/source answered with no body",
      new Headers(),
    );
  }
  const { html, css, js } = body.pieces;
  return { versionId: body.version_id, pieces: { html, css, js } };
}
